import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Clock, Upload, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoading, PageHeader, fmtDate } from "@/components/shared";
import { RequirementsChecklist, CompletenessRing } from "@/components/WorkerRequirements";
import { Plane as PlaneIcon, FileText as FileTextIcon } from "lucide-react";

function WorkerImmigrationCard() {
  const { t } = useTranslation();
  const [c, setC] = useState(undefined);
  useEffect(() => { api.get("/worker/immigration").then((r) => setC(r.data.case)).catch(() => setC(null)); }, []);
  if (!c) return null;
  const done = c.steps.filter((s) => s.status === "COMPLETED").length;
  return (
    <div className="card-soft mt-6 p-6" data-testid="worker-immigration">
      <div className="mb-2 flex items-center gap-2 font-heading font-bold text-navy"><PlaneIcon className="h-4 w-4 text-cyan-dark" /> {t("im.sectionTitle")}</div>
      <div className="mb-3 text-sm text-slate-600">{c.pathway_label} · {done}/{c.steps.length}</div>
      <div className="space-y-1.5">
        {c.steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className={`h-2 w-2 rounded-full ${s.status === "COMPLETED" ? "bg-emerald-500" : s.status === "IN_PROGRESS" ? "bg-cyan" : "bg-slate-300"}`} />
            <span className={s.status === "COMPLETED" ? "text-slate-400 line-through" : "text-navy"}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkerContractCard() {
  const [c, setC] = useState(undefined);
  useEffect(() => { api.get("/worker/contract").then((r) => setC(r.data.contract)).catch(() => setC(null)); }, []);
  if (!c) return null;
  return (
    <div className="card-soft mt-6 p-6" data-testid="worker-contract">
      <div className="mb-2 flex items-center gap-2 font-heading font-bold text-navy"><FileTextIcon className="h-4 w-4 text-cyan-dark" /> Arbeitsvertrag</div>
      <div className="grid gap-1 text-sm">
        <div className="flex justify-between border-b border-slate-50 py-1.5"><span className="text-slate-400">Version</span><span className="font-medium text-navy">v{c.version} · {c.status}</span></div>
        <div className="flex justify-between border-b border-slate-50 py-1.5"><span className="text-slate-400">Position</span><span className="font-medium text-navy">{c.job_title || "—"}</span></div>
        <div className="flex justify-between border-b border-slate-50 py-1.5"><span className="text-slate-400">Beginn</span><span className="font-medium text-navy">{c.start_date || "—"}</span></div>
        {c.salary && <div className="flex justify-between py-1.5"><span className="text-slate-400">Vergütung</span><span className="font-medium text-navy">{c.salary}</span></div>}
      </div>
    </div>
  );
}
import { COUNTRIES } from "@/lib/countries";
import api, { formatApiErrorDetail } from "@/lib/api";

const LANGS = ["English", "Deutsch", "Bosanski / Hrvatski / Srpski", "Shqip", "Other"];
const CONFIRM_FIELDS = [
  { key: "nationality", label: "Staatsangehörigkeit", type: "country" },
  { key: "date_of_birth", label: "Geburtsdatum", type: "date" },
  { key: "passport_number", label: "Reisepass-Nr.", type: "text" },
  { key: "passport_country", label: "Pass-Land", type: "country" },
  { key: "passport_valid_until", label: "Pass gültig bis", type: "date" },
];
const DOC_STATUS = {
  PENDING: { c: "bg-amber-100 text-amber-700", t: "Warten auf Prüfung" },
  VERIFIED: { c: "bg-emerald-100 text-emerald-700", t: "Bestätigt" },
  REJECTED: { c: "bg-red-100 text-red-700", t: "Abgelehnt" },
};

function RO({ label, value }) { return <div className="flex justify-between border-b border-slate-50 py-2.5 text-sm"><span className="text-slate-400">{label}</span><span className="font-medium text-navy">{value || "—"}</span></div>; }

export default function WorkerProfile() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [f, setF] = useState({ phone: "", address: "", preferred_language: "" });
  const [pf, setPf] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("passport");
  const [uploading, setUploading] = useState(false);

  const loadDocs = () => api.get("/worker/documents").then((r) => setDocs((r.data.documents || []).filter((d) => d.source === "WORKER"))).catch(() => {});
  const load = () => api.get("/worker/profile").then((r) => {
    setData(r.data);
    if (r.data.source_type === "INTERNAL") {
      const e = r.data.employee;
      setF({ phone: e.phone || "", address: e.address || "", preferred_language: e.preferred_language || "" });
    }
  });
  useEffect(() => { load(); loadDocs(); }, []);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const setP = (k) => (v) => setPf((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/worker/profile", f); toast.success(t("worker.saveChanges")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const submitProposal = async () => {
    const fields = Object.fromEntries(Object.entries(pf).filter(([, v]) => v && String(v).trim()));
    if (!Object.keys(fields).length) { toast.error("Bitte mindestens ein Feld ausfüllen."); return; }
    setSubmitting(true);
    try { await api.post("/worker/profile/change-request", { fields }); toast.success("Zur Bestätigung eingereicht"); setPf({}); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const cancelProposal = async (id) => {
    try { await api.post(`/worker/profile/change-request/${id}/cancel`); toast.success("Zurückgezogen"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", docType);
    setUploading(true);
    try { await api.post("/worker/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Hochgeladen — wartet auf Prüfung"); loadDocs(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  if (!data) return <PageLoading />;

  if (data.source_type === "SUBCONTRACTOR") {
    const w = data.worker;
    return (
      <div data-testid="worker-profile">
        <PageHeader title={t("worker.myProfile")} />
        <div className="card-soft max-w-xl p-6">
          <div className="mb-4 font-heading font-bold text-navy">{t("worker.managed")}</div>
          <RO label={t("worker.name")} value={`${w.first_name} ${w.last_name}`} />
          <RO label={t("worker.workerId")} value={w.worker_id} />
          <RO label={t("worker.nationality")} value={w.nationality} />
          <RO label={t("worker.passportUntil")} value={fmtDate(w.passport_expiry)} />
          <p className="mt-4 text-xs text-slate-400">{t("worker.managedNote")}</p>
        </div>
        {data.requirements && (
          <div className="card-soft mt-6 max-w-xl p-6" data-testid="worker-requirements">
            <div className="mb-3 flex items-center justify-between"><div className="font-heading font-bold text-navy">{t("p2.requiredDocs")}</div>{data.requirements.completeness && <CompletenessRing pct={data.requirements.completeness.pct} size={52} />}</div>
            <RequirementsChecklist reqs={data.requirements} />
          </div>
        )}
        <WorkerImmigrationCard />
        <WorkerContractCard />
      </div>
    );
  }
  const e = data.employee;
  const pending = data.pending_change;
  const lastRej = data.last_rejected;
  const verif = data.field_verifications || {};
  return (
    <div data-testid="worker-profile">
      <PageHeader title={t("worker.myProfile")} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card-soft p-6">
          <div className="mb-4 font-heading font-bold text-navy">{t("worker.editable")}</div>
          <div className="space-y-4">
            <div className="space-y-2"><Label>{t("worker.phone")}</Label><Input value={f.phone} onChange={(ev) => set("phone")(ev.target.value)} data-testid="worker-phone" /></div>
            <div className="space-y-2"><Label>{t("worker.address")}</Label><Input value={f.address} onChange={(ev) => set("address")(ev.target.value)} data-testid="worker-address" /></div>
            <div className="space-y-2"><Label>{t("worker.prefLang")}</Label>
              <Select value={f.preferred_language} onValueChange={set("preferred_language")}><SelectTrigger data-testid="worker-language"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
            </div>
            <Button onClick={save} disabled={saving} className="rounded-full bg-navy text-white" data-testid="worker-save-profile">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("worker.saveChanges")}</Button>
          </div>
        </div>
        <div className="card-soft p-6">
          <div className="mb-4 font-heading font-bold text-navy">{t("worker.managed")}</div>
          <RO label={t("worker.name")} value={`${e.first_name} ${e.last_name}`} />
          <RO label={t("worker.workerId")} value={e.worker_id} />
          <RO label={t("worker.nationality")} value={e.nationality} />
          <RO label={t("worker.residenceStatus")} value={e.residence_status} />
          <RO label={t("worker.permitUntil")} value={fmtDate(e.permit_valid_until)} />
          <RO label={t("worker.passportUntil")} value={fmtDate(e.passport_valid_until)} />
          <p className="mt-4 text-xs text-slate-400">{t("worker.managedNote")}</p>
        </div>
      </div>

      {data.requirements && (
        <div className="card-soft mt-6 p-6" data-testid="worker-requirements">
          <div className="mb-3 flex items-center justify-between"><div className="font-heading font-bold text-navy">{t("p2.requiredDocs")}</div>{data.requirements.completeness && <CompletenessRing pct={data.requirements.completeness.pct} size={52} />}</div>
          <RequirementsChecklist reqs={data.requirements} />
        </div>
      )}
      <WorkerImmigrationCard />
      <WorkerContractCard />

      <div className="card-soft mt-6 p-6" data-testid="worker-confirm-card">
        <div className="mb-1 font-heading font-bold text-navy">Daten zur Bestätigung einreichen</div>
        <p className="mb-4 text-xs text-slate-400">Diese Angaben werden erst nach Bestätigung durch Ihren Arbeitgeber offiziell gespeichert (Vier-Augen-Prinzip).</p>
        {pending && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="worker-profile-pending">
            <Clock className="h-4 w-4 text-amber-600" />
            <span className="text-amber-700">Warten auf Bestätigung: {Object.keys(pending.fields || {}).map((k) => (CONFIRM_FIELDS.find((c) => c.key === k)?.label || k)).join(", ")}</span>
            <button onClick={() => cancelProposal(pending.id)} className="ml-auto text-xs font-semibold text-amber-700 underline" data-testid="worker-cancel-proposal">Zurückziehen</button>
          </div>
        )}
        {!pending && lastRej && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm" data-testid="worker-profile-rejected">
            <div className="font-semibold text-red-700">Abgelehnt: {Object.keys(lastRej.fields || {}).map((k) => (CONFIRM_FIELDS.find((c) => c.key === k)?.label || k)).join(", ")}</div>
            {lastRej.review_note && <div className="mt-0.5 text-xs text-red-600">Grund: {lastRej.review_note}</div>}
            <div className="mt-1 text-[11px] text-red-500">Bitte korrigieren und erneut einreichen.</div>
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          {CONFIRM_FIELDS.map((c) => {
            const confirmed = verif[c.key]?.status === "CONFIRMED";
            return (
              <div key={c.key} className="space-y-1.5">
                <Label className="flex items-center gap-1.5">{c.label}
                  {confirmed && <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`verif-${c.key}`}><CheckCircle2 className="h-3 w-3" /> bestätigt</span>}
                </Label>
                {c.type === "country" ? (
                  <Select value={pf[c.key] || ""} onValueChange={setP(c.key)}>
                    <SelectTrigger data-testid={`propose-${c.key}`}><SelectValue placeholder={e[c.key] ? String(e[c.key]) : "— wählen —"} /></SelectTrigger>
                    <SelectContent className="max-h-72">{COUNTRIES.map((co) => <SelectItem key={co} value={co}>{co}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input type={c.type} value={pf[c.key] || ""} placeholder={e[c.key] ? String(e[c.key]) : "—"} onChange={(ev) => setP(c.key)(ev.target.value)} data-testid={`propose-${c.key}`} />
                )}
              </div>
            );
          })}
        </div>
        <Button onClick={submitProposal} disabled={submitting} className="mt-5 rounded-full bg-cyan-dark text-white" data-testid="worker-submit-change-request">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Zur Bestätigung einreichen"}
        </Button>
      </div>

      <div className="card-soft mt-6 p-6" data-testid="worker-documents-card">
        <div className="mb-1 font-heading font-bold text-navy">Dokumente hochladen</div>
        <p className="mb-4 text-xs text-slate-400">Reisepass oder Aufenthaltstitel fotografieren/hochladen. Der Arbeitgeber prüft; das Ablaufdatum wird nach Bestätigung in die Compliance übernommen.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5"><Label>Dokumenttyp</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="w-56" data-testid="doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="passport">Reisepass</SelectItem>
                <SelectItem value="residence_permit">Aufenthaltstitel / Dozvola</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white" data-testid="doc-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Datei wählen
            <input type="file" accept="image/*,.pdf" className="hidden" disabled={uploading} onChange={(ev) => uploadDoc(ev.target.files?.[0])} data-testid="doc-file-input" />
          </label>
        </div>
        <div className="mt-5 space-y-2">
          {docs.length === 0 ? <p className="text-sm text-slate-400" data-testid="doc-empty">Noch keine Dokumente hochgeladen.</p> :
            docs.map((d) => {
              const st = DOC_STATUS[d.verification_status] || DOC_STATUS.PENDING;
              return (
                <div key={d.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-100 p-3 text-sm" data-testid={`doc-row-${d.id}`}>
                  <FileText className="h-4 w-4 text-slate-400" />
                  <span className="font-medium text-navy">{d.type === "passport" ? "Reisepass" : "Aufenthaltstitel"}</span>
                  <span className="text-xs text-slate-400">{d.filename}</span>
                  {d.expiry_date && <span className="text-xs text-slate-500">gültig bis {fmtDate(d.expiry_date)}</span>}
                  <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.c}`}>{st.t}</span>
                  {d.verification_status === "REJECTED" && d.review_note && <span className="w-full text-[11px] text-red-500" data-testid={`doc-reject-reason-${d.id}`}>Grund: {d.review_note}</span>}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
